import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Clock3,
  Database,
  FileText,
  MessageSquareText,
} from 'lucide-react';
import { ChatMarkdownRenderer } from '@/components/chat/ChatMarkdownRenderer';
import { ExecutionLaunchInputs } from '@/components/execution/ExecutionLaunchInputs';
import { ExecutionTimelineRail } from '@/components/execution/ExecutionTimelineRail';
import { RawJsonRenderer } from '@/components/execution/renderers/RawJsonRenderer';
import type { LaunchInput } from '@/lib/execution-launch-inputs';
import { cn } from '@/lib/utils';
import { getActionMeta } from '@shared/types/domino-actions';
import type {
  ExecutionStep,
  ExecutionStepStatus,
  FlowExecutionStatus,
} from '@shared/types/flow-executions';
import type { ExecutionChatMessageRow } from '@shared/types/execution-chat';
import { getStepWarnings } from '@/components/execution/step-warnings';

interface SnapshotDomino {
  position?: number;
  action_type?: string;
  label?: string;
}

interface ToolExecutionTracePanelProps {
  executionId: string;
  executionStatus: FlowExecutionStatus;
  steps: ExecutionStep[];
  messages: ExecutionChatMessageRow[];
  snapshotDominos?: SnapshotDomino[];
  launchInputs?: LaunchInput[];
  loading?: boolean;
  error?: string | null;
}

interface TraceEntry {
  id: string;
  stepId: string | null;
  actor: 'assistant' | 'system';
  title: string;
  text: string;
  createdAt: string;
}

const STEP_STATUS_LABELS: Record<ExecutionStepStatus, string> = {
  pending: 'En attente',
  running: 'En cours',
  waiting_hitl: 'Validation requise',
  completed: 'Exécuté',
  failed: 'En erreur',
  skipped: 'Ignoré',
};

const STEP_STATUS_CLASS_NAMES: Record<ExecutionStepStatus, string> = {
  pending: 'text-domino-inkSoft',
  running: 'text-amber-700',
  waiting_hitl: 'text-orange-700',
  completed: 'text-emerald-700',
  failed: 'text-red-700',
  skipped: 'text-domino-inkSoft',
};

const RUN_STATUS_LABELS: Record<FlowExecutionStatus, string> = {
  pending: 'En file',
  running: 'En cours',
  paused: 'Action requise',
  completed: 'Terminé',
  failed: 'Erreur',
  cancelled: 'Annulé',
};

const TRACE_STREAM_CHUNK_MAX_CHARS = 72;
const TRACE_STREAM_CHUNK_DELAY_MS = 18;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, ms));

const chunkText = (text: string): string[] => {
  const tokens = text.match(/\S+\s*/g) ?? [];
  if (tokens.length === 0) return text.length > 0 ? [text] : [];

  const chunks: string[] = [];
  let current = '';

  for (const token of tokens) {
    if (
      current.length > 0 &&
      current.length + token.length > TRACE_STREAM_CHUNK_MAX_CHARS
    ) {
      chunks.push(current);
      current = '';
    }
    current += token;
  }

  if (current.length > 0) chunks.push(current);
  return chunks;
};

const formatTime = (iso: string | null): string => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getTraceMessageText = (message: ExecutionChatMessageRow): string | null => {
  switch (message.kind) {
    case 'assistant_message': {
      const payload = (message as ExecutionChatMessageRow<'assistant_message'>).payload;
      return payload.text.trim() || null;
    }
    case 'action_result': {
      const payload = (message as ExecutionChatMessageRow<'action_result'>).payload;
      return (
        [payload.title, payload.text]
          .filter((value): value is string => Boolean(value?.trim()))
          .join('\n\n') || null
      );
    }
    case 'system_event': {
      const payload = (message as ExecutionChatMessageRow<'system_event'>).payload;
      return payload.text.trim() || null;
    }
    default:
      return null;
  }
};

const isResultCardMessage = (message: ExecutionChatMessageRow): boolean => {
  if (message.kind !== 'assistant_message') return false;
  return Boolean(
    (message as ExecutionChatMessageRow<'assistant_message'>).payload.metadata?.resultCard
  );
};

const resolveStepLabel = (
  step: ExecutionStep,
  snapshotDominos: SnapshotDomino[] | undefined
): string => {
  const fromSnapshot =
    snapshotDominos?.find((domino) => domino.position === step.position)?.label ??
    snapshotDominos?.[step.position - 1]?.label;
  if (typeof fromSnapshot === 'string' && fromSnapshot.trim().length > 0) {
    return fromSnapshot.trim();
  }

  const meta = getActionMeta(step.action_type as never);
  return meta?.label ?? step.action_type;
};

const buildStepSummary = (step: ExecutionStep): string => {
  if (step.status === 'running') return 'Travail en cours sur cette étape.';
  if (step.status === 'waiting_hitl')
    return 'Domino attend une validation ou une précision.';
  if (step.status === 'failed') {
    const errorMessage =
      typeof step.error?.message === 'string'
        ? step.error.message
        : typeof step.error?.error === 'string'
          ? step.error.error
          : null;
    return errorMessage ?? 'Cette étape a échoué.';
  }
  if (step.status === 'completed' && step.output) {
    const textValue = Object.entries(step.output).find(
      ([, value]) => typeof value === 'string' && value.trim().length > 0
    )?.[1];
    if (typeof textValue === 'string') {
      const compact = textValue.replace(/\s+/g, ' ').trim();
      return compact.length > 160 ? `${compact.slice(0, 157).trim()}...` : compact;
    }

    const fieldCount = Object.keys(step.output).length;
    return fieldCount > 0
      ? `${fieldCount} champ${fieldCount > 1 ? 's' : ''} produit${fieldCount > 1 ? 's' : ''}.`
      : 'Étape terminée.';
  }
  if (step.status === 'skipped') return 'Étape ignorée.';
  return 'En attente.';
};

export const ToolExecutionTracePanel = ({
  executionId,
  executionStatus,
  steps,
  messages,
  snapshotDominos,
  launchInputs = [],
  loading = false,
  error = null,
}: ToolExecutionTracePanelProps) => {
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [highlightedStepId, setHighlightedStepId] = useState<string | null>(null);
  const [streamedEntryTextById, setStreamedEntryTextById] = useState<
    Record<string, string>
  >({});
  const entryRefs = useRef(new Map<string, HTMLDivElement>());
  const stepDetailRef = useRef<HTMLDivElement | null>(null);
  const seenEntryIdsRef = useRef(new Set<string>());
  const initialEntriesLoadedRef = useRef(false);
  const activeExecutionIdRef = useRef(executionId);

  const orderedSteps = useMemo(
    () => [...steps].sort((left, right) => left.position - right.position),
    [steps]
  );
  const stepById = useMemo(
    () => new Map(orderedSteps.map((step) => [step.id, step])),
    [orderedSteps]
  );
  const stepLabels = useMemo(
    () =>
      new Map(
        orderedSteps.map((step) => [step.id, resolveStepLabel(step, snapshotDominos)])
      ),
    [orderedSteps, snapshotDominos]
  );
  const traceEntries = useMemo<TraceEntry[]>(
    () =>
      messages
        .filter((message) => !isResultCardMessage(message))
        .map((message) => {
          const text = getTraceMessageText(message);
          if (!text) return null;
          const step = message.step_id ? stepById.get(message.step_id) : null;
          return {
            id: message.id,
            stepId: message.step_id,
            actor: message.actor === 'system' ? 'system' : 'assistant',
            title: step
              ? `Étape ${step.position} · ${stepLabels.get(step.id) ?? 'Synthèse'}`
              : 'Synthèse du run',
            text,
            createdAt: message.created_at,
          };
        })
        .filter((entry): entry is TraceEntry => entry !== null),
    [messages, stepById, stepLabels]
  );

  useEffect(() => {
    activeExecutionIdRef.current = executionId;
    seenEntryIdsRef.current = new Set();
    initialEntriesLoadedRef.current = false;
    setStreamedEntryTextById({});
  }, [executionId]);

  useEffect(() => {
    if (loading) return;

    if (!initialEntriesLoadedRef.current) {
      for (const entry of traceEntries) {
        seenEntryIdsRef.current.add(entry.id);
      }
      initialEntriesLoadedRef.current = true;
      return;
    }

    const shouldStream = executionStatus === 'pending' || executionStatus === 'running';

    for (const entry of traceEntries) {
      if (seenEntryIdsRef.current.has(entry.id)) continue;
      seenEntryIdsRef.current.add(entry.id);

      if (
        !shouldStream ||
        entry.actor !== 'assistant' ||
        entry.text.trim().length === 0
      ) {
        continue;
      }

      const entryExecutionId = executionId;
      void (async () => {
        let accumulated = '';
        setStreamedEntryTextById((current) => ({
          ...current,
          [entry.id]: '',
        }));

        for (const chunk of chunkText(entry.text)) {
          if (activeExecutionIdRef.current !== entryExecutionId) return;
          accumulated += chunk;
          setStreamedEntryTextById((current) => ({
            ...current,
            [entry.id]: accumulated,
          }));
          await sleep(TRACE_STREAM_CHUNK_DELAY_MS);
        }

        if (activeExecutionIdRef.current !== entryExecutionId) return;
        setStreamedEntryTextById((current) => {
          const next = { ...current };
          delete next[entry.id];
          return next;
        });
      })();
    }
  }, [executionId, executionStatus, loading, traceEntries]);

  const selectedStep =
    (selectedStepId ? stepById.get(selectedStepId) : null) ??
    orderedSteps.find((step) => step.status === 'running') ??
    orderedSteps[orderedSteps.length - 1] ??
    null;

  useEffect(() => {
    if (!selectedStepId && selectedStep) {
      setSelectedStepId(selectedStep.id);
    }
  }, [selectedStep, selectedStepId]);

  const focusStep = (step: ExecutionStep) => {
    setSelectedStepId(step.id);
    setHighlightedStepId(step.id);
    window.setTimeout(() => setHighlightedStepId(null), 1_400);

    window.requestAnimationFrame(() => {
      const entry = traceEntries.find((item) => item.stepId === step.id);
      const target = entry ? entryRefs.current.get(entry.id) : stepDetailRef.current;
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <div className="mx-5 lg:mx-6">
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px] xl:items-start">
        <div className="min-w-0 space-y-5">
          <div className="rounded-[24px] border border-domino-cardBorder bg-domino-white px-5 py-4 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="text-2xl font-semibold tracking-tight text-domino-ink">
                  Détail du traitement
                </h3>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-domino-inkSoft">
                  Ce qui a été retenu à chaque étape.
                </p>
              </div>
              <p className="text-xs text-domino-inkSoft">
                {RUN_STATUS_LABELS[executionStatus]} · {orderedSteps.length} étape
                {orderedSteps.length > 1 ? 's' : ''}
              </p>
            </div>
          </div>

          <ExecutionLaunchInputs inputs={launchInputs} loading={loading} />

          {error && (
            <div className="rounded-[18px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>{error}</p>
              </div>
            </div>
          )}

          {loading && traceEntries.length === 0 && (
            <div className="rounded-[24px] border border-domino-cardBorder bg-domino-white px-5 py-8">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-domino-cardBorder border-t-domino-aubergine" />
              <p className="mt-3 text-sm text-domino-inkSoft">
                Chargement de la trace d’exécution...
              </p>
            </div>
          )}

          {traceEntries.length > 0 ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <MessageSquareText className="h-4 w-4 text-domino-coral" />
                <h4 className="text-sm font-semibold text-domino-ink">
                  Synthèses d’étapes
                </h4>
              </div>
              {traceEntries.map((entry) => {
                const highlighted =
                  highlightedStepId && entry.stepId === highlightedStepId;
                return (
                  <div
                    key={entry.id}
                    ref={(node) => {
                      if (node) {
                        entryRefs.current.set(entry.id, node);
                      } else {
                        entryRefs.current.delete(entry.id);
                      }
                    }}
                    className={cn(
                      'scroll-mt-5 rounded-[22px] border bg-domino-white px-5 py-4 shadow-sm transition-colors',
                      highlighted
                        ? 'border-domino-coral bg-domino-lilac'
                        : 'border-domino-cardBorder'
                    )}
                  >
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-2xl bg-domino-lilac text-domino-aubergine">
                          {entry.actor === 'system' ? (
                            <Clock3 className="h-4 w-4" />
                          ) : (
                            <FileText className="h-4 w-4" />
                          )}
                        </span>
                        <p className="min-w-0 break-words text-sm font-semibold text-domino-ink">
                          {entry.title}
                        </p>
                      </div>
                      <span className="text-xs text-domino-inkSoft">
                        {formatTime(entry.createdAt)}
                      </span>
                    </div>
                    <ChatMarkdownRenderer
                      content={streamedEntryTextById[entry.id] ?? entry.text}
                    />
                    {entry.id in streamedEntryTextById && (
                      <span className="ml-0.5 inline-block h-3.5 w-1.5 translate-y-0.5 animate-pulse rounded-sm bg-domino-ink/40" />
                    )}
                  </div>
                );
              })}
            </div>
          ) : !loading ? (
            <div className="rounded-[24px] border border-dashed border-domino-cardBorder bg-domino-white px-5 py-8">
              <h4 className="text-base font-semibold text-domino-ink">
                Pas encore de synthèse persistée
              </h4>
              <p className="mt-2 text-sm leading-6 text-domino-inkSoft">
                Les étapes restent consultables à droite. Les synthèses apparaîtront ici
                quand le run publiera ses messages d’analyse.
              </p>
            </div>
          ) : null}

          {selectedStep && (
            <div
              ref={stepDetailRef}
              className="scroll-mt-5 rounded-[24px] border border-domino-cardBorder bg-domino-white px-5 py-4 shadow-sm"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Database className="h-4 w-4 text-domino-coral" />
                    <h4 className="text-sm font-semibold text-domino-ink">
                      Détail de l’étape sélectionnée
                    </h4>
                  </div>
                  <p className="mt-2 break-words text-lg font-semibold tracking-tight text-domino-ink">
                    {selectedStep.position}. {stepLabels.get(selectedStep.id)}
                  </p>
                  <p className="mt-1 text-sm leading-6 text-domino-inkSoft">
                    {buildStepSummary(selectedStep)}
                  </p>
                </div>
                <span
                  className={cn(
                    'text-xs font-medium',
                    STEP_STATUS_CLASS_NAMES[selectedStep.status]
                  )}
                >
                  {STEP_STATUS_LABELS[selectedStep.status]}
                </span>
              </div>

              {getStepWarnings(selectedStep).length > 0 && (
                <ul className="mt-3 space-y-1 rounded-[18px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  {getStepWarnings(selectedStep).map((warning, index) => (
                    <li key={`${index}-${warning}`}>⚠ {warning}</li>
                  ))}
                </ul>
              )}

              <div className="mt-4 grid gap-3 lg:grid-cols-2">
                <details className="rounded-[18px] border border-domino-cardBorder bg-domino-muted px-4 py-3">
                  <summary className="cursor-pointer text-sm font-semibold text-domino-ink">
                    Données utilisées
                  </summary>
                  <RawJsonRenderer
                    data={selectedStep.resolved_input}
                    className="mt-3 bg-domino-white"
                  />
                </details>

                <details
                  className="rounded-[18px] border border-domino-cardBorder bg-domino-muted px-4 py-3"
                  open={selectedStep.status === 'failed'}
                >
                  <summary className="cursor-pointer text-sm font-semibold text-domino-ink">
                    Résultat de l’étape
                  </summary>
                  <RawJsonRenderer
                    data={selectedStep.error ?? selectedStep.output ?? {}}
                    className="mt-3 bg-domino-white"
                  />
                </details>
              </div>
            </div>
          )}
        </div>

        <aside className="xl:sticky xl:top-6">
          <ExecutionTimelineRail
            steps={orderedSteps}
            snapshotDominos={snapshotDominos}
            executionStatus={executionStatus}
            selectedStepId={selectedStep?.id ?? null}
            onStepSelect={focusStep}
          />
        </aside>
      </div>
    </div>
  );
};
